import cx from "classnames";
import React, { useMemo } from "react";

import { StateCallbacks } from "../../state";
import { ContentType, State } from "../../types";
import { InnodomContent } from "../InnodomContent";
import { ScrollToElement } from "../ScrollToElement";

type Props = Readonly<{
  confidenceThreshold: State["confidenceThreshold"];
  confirmTableSelection: boolean;
  disableEditing: boolean;
  disableTableSelectionConfirmation: () => void;
  onAddCorrections: StateCallbacks["onAddCorrections"];
  onSelectBlock: StateCallbacks["onSelectBlock"];
  setShowReferences: (blockId: string) => void;
  state: State;
}>;

export const Text = ({
  confirmTableSelection,
  disableEditing,
  disableTableSelectionConfirmation,
  onAddCorrections,
  onSelectBlock,
  setShowReferences,
  state,
}: Props) => {
  const { activeValue, blocks, fontInformation, pages, values } = state;
  const contentOrder: Array<string> = useMemo(
    () =>
      pages.reduce((prev, page) => {
        return prev.concat(page.blocks);
      }, [] as Array<string>),
    [pages],
  );
  let selector = null;
  if (activeValue in values && values[activeValue].idRef) {
    selector = `support-${values[activeValue].idRef}`;
  }
  const wrapperClassName = cx("blocks", "h-100", "overflow-auto", "p-3", {
    selecting: !!activeValue,
  });
  return ( 
    <ScrollToElement
      activeDataPoint={state.scrollToBlock}
      className={wrapperClassName}
      selector={selector}
    >
      {contentOrder.map(contentId => {
        if (!(contentId in blocks)) {
          return null;
        }
        const block = blocks[contentId];
        const selected = activeValue in values && values[activeValue].idRef === block.id;
        let selectedCell = null;
        if (activeValue in values) {
          const idRef = values[activeValue].idRef;
          if (idRef !== null && idRef in blocks && blocks[idRef].type === ContentType.TableCell) {
            selectedCell = blocks[idRef].id;
          }
        }
        let referencesLink;
        if (block.references.length > 0) {
          referencesLink = (
            <small className="d-block text-right">
              <a
                href="#"
                id={`link-${block.id}`}
                onClick={e => {
                  e.preventDefault();
                  e.stopPropagation();
                  setShowReferences(block.id);
                }}
              >
                Data Points ({block.references.length})
              </a>
            </small>
          ); 
        }
        return (
          <InnodomContent
            block={block}
            // Passing blocks down causes a full re-render of all Texts whenever 
            // a block changes and slows down rendering by 500% 
            // It becomes noticeable for large docs (sluggish while typing in an Answer).
            // The reason blocks are passed is because table cells are stored 
            // as regular blocks in a flat structure.  To improve this, 
            // we'd have to change the table block to contain its cell blocks 
            // instead of making the Table widget do a lookup in the full list of blocks.
            // Then none of these InnodomContent would require to know about other blocks
            // and rendering would be skipped for all unchanged InnodomContents.  -SB
            blocks={blocks} 
            confirmTableSelection={confirmTableSelection}
            disableTableSelectionConfirmation={disableTableSelectionConfirmation}
            fontInformation={fontInformation}
            id={`support-${block.id}`}
            key={block.id}
            onAddCorrections={disableEditing ? undefined : onAddCorrections}
            onSelectBlock={onSelectBlock}
            pages={pages}
            referencesLink={referencesLink}
            selected={selected}
            selectedCell={selectedCell}
            setShowReferences={setShowReferences}
          /> 
        );
      })}
    </ScrollToElement>
  );
};
Text.displayName = "Text";
